import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import api from '../api/client';
import { toMiamiDateString, formatMiamiShort, getMiamiWeeksOfYear, miamiDateFromString } from '../utils/dateUtils';

// Day abbreviations Mon–Sun
const DAY_ABBR = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// ── Comment popup ──────────────────────────────────────────────────────────────
export const CommentPopup = ({ comment, onSave, onClose }) => {
  const [val, setVal] = useState(comment || '');
  return (
    <div className="ptp-comment-popup">
      <div className="ptp-comment-popup-header">
        <span><i className="fas fa-comment-alt" style={{ marginRight: 6 }}></i>Comment</span>
        <button className="ptp-comment-close" onClick={onClose}>
          <i className="fas fa-times"></i>
        </button>
      </div>
      <textarea
        className="ptp-comment-textarea"
        placeholder="Why was the plan not fulfilled?"
        value={val}
        onChange={e => setVal(e.target.value)}
        autoFocus
        rows={3}
      />
      <div className="ptp-comment-actions">
        <button className="ptp-comment-save" onClick={() => onSave(val)}>Save</button>
        <button className="ptp-comment-cancel" onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
};

// ── Main component ─────────────────────────────────────────────────────────────
const MAX_WEEKS = 6;

const WeeklyPlanTable = ({ products, dateRange }) => {
  const currentYear = new Date().getFullYear();
  const allWeeks = useMemo(() => getMiamiWeeksOfYear(currentYear), [currentYear]);

  // Filter weeks by dateRange if set, then cap to MAX_WEEKS
  const { weeks, capped } = useMemo(() => {
    let filtered;
    if (!dateRange) {
      // No range — show current week only to avoid freeze
      const todayStr = toMiamiDateString(new Date());
      const cur = allWeeks.filter(w =>
        toMiamiDateString(w.start) <= todayStr && toMiamiDateString(w.end) >= todayStr
      );
      filtered = cur.length > 0 ? cur : allWeeks.slice(-1);
    } else {
      filtered = allWeeks.filter(w =>
        toMiamiDateString(w.start) >= dateRange.start &&
        toMiamiDateString(w.end)   <= dateRange.end
      );
    }
    const capped = filtered.length > MAX_WEEKS;
    return { weeks: filtered.slice(0, MAX_WEEKS), capped };
  }, [allWeeks, dateRange]);

  const allDays = useMemo(() => weeks.flatMap(w => w.days), [weeks]);

  // State: key is `${sku}-${YYYY-MM-DD}` (Miami date)
  const [planData,    setPlanData]    = useState({});
  const [actualData,  setActualData]  = useState({});
  const [comments,    setComments]    = useState({});
  const [loadError,   setLoadError]   = useState(null);

  const [editingCell, setEditingCell] = useState(null);
  const [cellValue,   setCellValue]   = useState('');
  const [commentCell, setCommentCell] = useState(null); // { sku, dateISO }
  const [commentPos,  setCommentPos]  = useState({ top: 0, left: 0 });

  // ── Load data whenever displayed date window changes ──────────────────────
  useEffect(() => {
    if (allDays.length === 0) return;
    const dateFrom = toMiamiDateString(allDays[0]);
    const dateTo   = toMiamiDateString(allDays[allDays.length - 1]);
    setLoadError(null);

    const load = async () => {
      try {
        // axios throws on HTTP errors — map them back to { ok, status } so each
        // endpoint keeps its own handling; network errors still reach the outer catch
        const get = url => api.get(url).then(
          r => ({ ok: true, data: r.data }),
          err => { if (err.response) return { ok: false, status: err.response.status }; throw err; }
        );
        const [planRes, actualRes, commentsRes] = await Promise.all([
          get(`/plantoproduce/daily-plan?from=${dateFrom}&to=${dateTo}`),
          get(`/plantoproduce/daily-actual?from=${dateFrom}&to=${dateTo}`),
          get(`/plantoproduce/daily-comments?from=${dateFrom}&to=${dateTo}`),
        ]);

        if (planRes.ok) {
          const planArr = planRes.data;
          const planMap = {};
          planArr.forEach(({ sku, date, qty_to_produce }) => {
            if (sku && date) planMap[`${sku}-${date}`] = qty_to_produce;
          });
          setPlanData(planMap);
        } else {
          console.warn('[daily-plan] HTTP', planRes.status);
          setLoadError(`Plan data unavailable (${planRes.status}). Check server routes.`);
        }

        if (actualRes.ok) {
          const actualArr = actualRes.data;
          const actualMap = {};
          actualArr.forEach(({ sku, date, actual_qty }) => {
            if (sku && date) actualMap[`${sku}-${date}`] = Number(actual_qty) || 0;
          });
          setActualData(actualMap);
        } else {
          console.warn('[daily-actual] HTTP', actualRes.status);
        }

        if (commentsRes.ok) {
          const commentsArr = commentsRes.data;
          const commentMap = {};
          commentsArr.forEach(({ sku, date, comment }) => {
            if (sku && date) commentMap[`${sku}-${date}`] = comment;
          });
          setComments(commentMap);
        } else {
          console.warn('[daily-comments] HTTP', commentsRes.status);
        }

      } catch (err) {
        console.error('Error loading weekly plan:', err);
        setLoadError('Failed to load plan data. See console for details.');
      }
    };

    load();
  }, [weeks.length, dateRange]);

  // ── Save planned quantity ─────────────────────────────────────────────────
  const savePlanCell = useCallback(async (sku, dateISO, value) => {
    const qty = value.toString().trim() === '' ? 0 : (parseInt(value) || 0);
    setPlanData(prev => ({ ...prev, [`${sku}-${dateISO}`]: qty }));
    setEditingCell(null);
    try {
      await api.post('/plantoproduce/daily-plan', { sku, date: dateISO, qty_to_produce: qty });
    } catch (err) {
      console.error('Error saving daily plan:', err);
      alert('Failed to save. Please try again.');
    }
  }, []);

  // ── Save comment ──────────────────────────────────────────────────────────
  const saveComment = useCallback(async (sku, dateISO, text) => {
    setComments(prev => ({ ...prev, [`${sku}-${dateISO}`]: text }));
    setCommentCell(null);
    try {
      await api.post('/plantoproduce/daily-comment', { sku, date: dateISO, comment: text });
    } catch (err) {
      console.error('Error saving comment:', err);
    }
  }, []);

  // ── Keyboard handler for editing ─────────────────────────────────────────
  const handleKeyDown = (e, sku, dateISO) => {
    if (e.key === 'Enter')  savePlanCell(sku, dateISO, cellValue);
    if (e.key === 'Escape') setEditingCell(null);
  };

  // ── Week totals ───────────────────────────────────────────────────────────
  const getWeekTotals = (sku, week) => {
    let planned = 0, actual = 0;
    week.days.forEach(d => {
      const key = `${sku}-${toMiamiDateString(d)}`;
      planned += planData[key]   || 0;
      actual  += actualData[key] || 0;
    });
    return { planned, actual };
  };

  // ── Render ────────────────────────────────────────────────────────────────
  const emptyColSpan = 3 + weeks.reduce((sum, _) => sum + 7 * 2 + 2, 0);

  return (
    <div className="plan-table-container" style={{ position: 'relative' }}>
      {loadError && (
        <div className="ptp-load-error">
          <i className="fas fa-exclamation-triangle"></i> {loadError}
        </div>
      )}
      {capped && (
        <div className="ptp-weeks-cap-notice">
          <i className="fas fa-info-circle"></i>
          Showing first {MAX_WEEKS} weeks of the selected range. Narrow the range to see more.
        </div>
      )}

      <div className="plan-table-wrapper">
        <table className="plan-table weekly-table">
          <thead>
            {/* Row 1 — week bands */}
            <tr>
              <th rowSpan="3" className="sticky col-row header-sticky">#</th>
              <th rowSpan="3" className="sticky col-name header-sticky left-col2">Name</th>
              <th rowSpan="3" className="header-sticky col-sku">SKU</th>
              {weeks.map((week, wi) => (
                <th key={wi} colSpan={week.days.length * 2 + 2} className="week-header header-sticky">
                  <span className="week-label">
                    <i className="fas fa-calendar-week"></i>
                    Week {week.weekNum} &nbsp;·&nbsp; {formatMiamiShort(week.start)} – {formatMiamiShort(week.end)}
                  </span>
                </th>
              ))}
            </tr>

            {/* Row 2 — day bands + "Σ Week" */}
            <tr>
              {weeks.map((week, wi) => (
                <React.Fragment key={wi}>
                  {week.days.map((d, di) => {
                    const dateStr = toMiamiDateString(d);
                    return (
                      <th key={dateStr} colSpan="2"
                          className={`day-header header-sticky day-col-${di}`}>
                        <div className="day-header-inner">
                          <span className="day-abbr">{DAY_ABBR[di]}</span>
                          <span className="day-date">{formatMiamiShort(d)}</span>
                        </div>
                      </th>
                    );
                  })}
                  <th colSpan="2" className="week-sum-header header-sticky">
                    <div className="day-header-inner">
                      <span className="day-abbr">Σ Week</span>
                    </div>
                  </th>
                </React.Fragment>
              ))}
            </tr>

            {/* Row 3 — Plan / Done sub-headers */}
            <tr>
              {weeks.map((week, wi) => (
                <React.Fragment key={wi}>
                  {week.days.map((d, di) => (
                    <React.Fragment key={toMiamiDateString(d)}>
                      <th className={`subheader-plan header-sticky day-col-${di}`}>Plan</th>
                      <th className={`subheader-done header-sticky day-col-${di}`}>Done</th>
                    </React.Fragment>
                  ))}
                  <th className="subheader-plan header-sticky">Plan</th>
                  <th className="subheader-done header-sticky">Done</th>
                </React.Fragment>
              ))}
            </tr>
          </thead>

          <tbody>
            {products.length === 0 ? (
              <tr>
                <td colSpan={emptyColSpan} className="ptp-empty-row">
                  <div className="ptp-empty-state">
                    <i className="fas fa-search" style={{ fontSize: 22, opacity: 0.22 }}></i>
                    <span>No products match your filters</span>
                  </div>
                </td>
              </tr>
            ) : products.map((product, idx) => (
              <tr key={product.SKU} className="ptp-data-row">
                <td className="sticky col-row">{idx + 1}</td>
                <td className="sticky col-name left-col2" title={product.name}>
                  {product.name}
                </td>
                <td className="col-sku">{product.SKU}</td>

                {weeks.map((week, wi) => {
                  const { planned: wPlan, actual: wActual } = getWeekTotals(product.SKU, week);
                  return (
                    <React.Fragment key={wi}>
                      {week.days.map((d, di) => {
                        const dateISO  = toMiamiDateString(d);
                        const key      = `${product.SKU}-${dateISO}`;
                        const planVal  = planData[key]   || 0;
                        const actualVal = actualData[key] || 0;
                        const comment  = comments[key];
                        const isEditing = editingCell === key;
                        const missed   = planVal > 0 && actualVal < planVal;
                        const ok       = planVal > 0 && actualVal >= planVal;

                        return (
                          <React.Fragment key={dateISO}>
                            {/* ── Plan cell ── */}
                            <td
                              className={[
                                'qty-cell plan-cell',
                                `day-col-${di}`,
                                planVal  ? 'has-value' : '',
                                isEditing ? 'editing'   : '',
                              ].join(' ')}
                              onDoubleClick={() => {
                                if (!isEditing) {
                                  setEditingCell(key);
                                  setCellValue(planVal || '');
                                }
                              }}
                              title="Double-click to edit plan"
                            >
                              {isEditing ? (
                                <input
                                  type="number"
                                  min="0"
                                  className="cell-input"
                                  value={cellValue}
                                  onChange={e => setCellValue(e.target.value)}
                                  onKeyDown={e => handleKeyDown(e, product.SKU, dateISO)}
                                  onBlur={() => savePlanCell(product.SKU, dateISO, cellValue)}
                                  autoFocus
                                  onClick={e => e.stopPropagation()}
                                />
                              ) : (
                                <span className="cell-display">
                                  {planVal || '—'}
                                </span>
                              )}
                            </td>

                            {/* ── Actual cell ── */}
                            <td
                              className={[
                                'qty-cell actual-cell',
                                `day-col-${di}`,
                                actualVal ? 'has-value' : '',
                                ok     ? 'actual-ok'   : '',
                                missed ? 'actual-miss' : '',
                              ].join(' ')}
                              style={{ position: 'relative' }}
                            >
                              <span className="cell-display actual-val">
                                {actualVal || '—'}
                              </span>

                              {/* Comment dot indicator */}
                              {comment && (
                                <span
                                  className="comment-dot"
                                  title={comment}
                                >
                                  <i className="fas fa-comment-dots"></i>
                                </span>
                              )}

                              {/* Add/edit comment button — visible on hover when missed */}
                              {missed && (
                                <button
                                  className="comment-btn"
                                  title={comment ? 'Edit comment' : 'Add comment'}
                                  onClick={e => {
                                    const rect = e.currentTarget.closest('td').getBoundingClientRect();
                                    setCommentPos({
                                      top:  rect.bottom + window.scrollY  + 4,
                                      left: rect.left   + window.scrollX,
                                    });
                                    setCommentCell({ sku: product.SKU, dateISO });
                                  }}
                                >
                                  <i className={`fas fa-${comment ? 'pencil-alt' : 'plus'}`}></i>
                                </button>
                              )}
                            </td>
                          </React.Fragment>
                        );
                      })}

                      {/* ── Week totals ── */}
                      <td className={`week-summary-cell plan-sum${wPlan ? ' has-value' : ''}`}>
                        {wPlan || '—'}
                      </td>
                      <td className={[
                        'week-summary-cell actual-sum',
                        wPlan > 0 ? (wActual >= wPlan ? 'actual-ok' : 'actual-miss') : '',
                        wActual ? 'has-value' : '',
                      ].join(' ')}>
                        {wActual || '—'}
                      </td>
                    </React.Fragment>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Comment popup ── */}
      {commentCell && (
        <>
          <div
            className="ptp-comment-overlay"
            onClick={() => setCommentCell(null)}
          />
          <div
            className="ptp-comment-popup-wrapper"
            style={{
              position: 'fixed',
              top:  Math.min(commentPos.top,  window.innerHeight - 230),
              left: Math.min(commentPos.left, window.innerWidth  - 310),
              zIndex: 600,
            }}
          >
            <CommentPopup
              comment={comments[`${commentCell.sku}-${commentCell.dateISO}`]}
              onSave={text => saveComment(commentCell.sku, commentCell.dateISO, text)}
              onClose={() => setCommentCell(null)}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default WeeklyPlanTable;